import React, { PropsWithChildren, useEffect, useState } from "react";

import { FOOTER } from "@config/testIds/footer";
import { IS_BROWSER } from "@config/consts";

import { AVAILABLE_COUNTRIES } from "@typings/globals";

import { TestWrapper } from "@ui/Atoms/TestWrapper";

import { getCountriesList } from "@services/Localisation";

import { useTranslate } from "@hooks/useTranslate";

import * as Styled from "./CountrySelectorSSR.styled";

const COUNTRY_SORT_ORDER = [
  AVAILABLE_COUNTRIES.AT,
  AVAILABLE_COUNTRIES.BE,
  AVAILABLE_COUNTRIES.CH,
  AVAILABLE_COUNTRIES.DE,
  AVAILABLE_COUNTRIES.FR,
  AVAILABLE_COUNTRIES.LU,
  AVAILABLE_COUNTRIES.NL,
  AVAILABLE_COUNTRIES.PL,
  AVAILABLE_COUNTRIES.IT,
];

type Props = PropsWithChildren<{ country: AVAILABLE_COUNTRIES }>;

export const CountrySelectorSSR: React.FC<Props> = ({ children, country }) => {
  const [initialized, setInitialized] = useState(false);
  const translate = useTranslate();
  const countries = getCountriesList(translate).sort(
    (a, b) =>
      COUNTRY_SORT_ORDER.indexOf(a.value) - COUNTRY_SORT_ORDER.indexOf(b.value),
  );
  const currentCountry = countries.find(i => i.value === country);

  useEffect(() => {
    if (!IS_BROWSER) return;

    setInitialized(true);
    (window as any).removeCountrySelectorHandler?.();
  }, []);

  if (initialized && children) {
    return <React.Fragment>{children}</React.Fragment>;
  }

  return (
    <Styled.Root>
      <Styled.EarthHomeWorldIcon />
      <Styled.Select id="country-selector" defaultValue={currentCountry?.value}>
        {countries.map(country => (
          <TestWrapper
            key={country.value}
            testId={`${FOOTER.COUNTRY_SELECT_OPTION}_${country.value}`}
          >
            <Styled.Option value={country.value}>{country.label}</Styled.Option>
          </TestWrapper>
        ))}
      </Styled.Select>
      <script
        type="text/javascript"
        dangerouslySetInnerHTML={{
          __html: `
        const countrySelector = document.getElementById('country-selector');
        
        const countryToDomain = (country) => {  
            switch (country) {
               case '${AVAILABLE_COUNTRIES.CH}':
                 return '${process.env.DOMAIN_CH}';
               case '${AVAILABLE_COUNTRIES.LU}':
                 return '${process.env.DOMAIN_LU}';
               case '${AVAILABLE_COUNTRIES.FR}':
                 return '${process.env.DOMAIN_FR}';
               case '${AVAILABLE_COUNTRIES.DE}':
                 return '${process.env.DOMAIN_DE}';
               case '${AVAILABLE_COUNTRIES.IT}':
                 return '${process.env.DOMAIN_IT}';
               case '${AVAILABLE_COUNTRIES.BE}':
                 return '${process.env.DOMAIN_BE}';
               case '${AVAILABLE_COUNTRIES.NL}':
                 return '${process.env.DOMAIN_NL}';
               default:
                 return '${process.env.DOMAIN_CH}';
              }
            }

        const countrySelecterHandler = (e) => {
          const domain = 'https://' + countryToDomain(e.target.value);
          window.location.href = domain;
        }    

        countrySelector.addEventListener('change', countrySelecterHandler);

        window.removeCountrySelectorHandler = () => {
            countrySelector.removeEventListener('change', countrySelecterHandler);
            window.removeCountrySelectorHandler = undefined;
        }
        `,
        }}
      />
    </Styled.Root>
  );
};
